import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import moment from "moment"

import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"
import LoadingWrapper from "~/library/LoadingWrapper"
import Pagination from "~/library/Pagination"
import useFetchResource from "~/hooks/useFetchResource"
import { apiExports, apiWorkspaceStripeConnectedPayouts } from "~/routes"
import getAppConfig from "~/utils/getAppConfig"
import { formatCurrency } from "~/utils/helpers"
import { useToast } from "~/utils/Toast"
import type { StripeConnectedPayoutEntry } from "~/types/StripeConnectedPayout"

interface Props {
  endDate?: string
  startDate?: string
}

const PayoutReconciliationReport: React.FC<Props> = ({ endDate, startDate }) => {
  const { venueSlug } = getAppConfig()
  const toast = useToast()
  const [isExporting, setIsExporting] = useState(false)
  const [filters, setFilters] = useState({
    connected_account: "",
    currency: "",
    reconciliation_status: "",
    status: "",
  })
  const endpoint = apiWorkspaceStripeConnectedPayouts.reconciliation.path({ venueSlug })
  const {
    data: entries,
    isLoading,
    metadata,
    refetch,
  } = useFetchResource<{ entries: StripeConnectedPayoutEntry[] }, "entries">(endpoint, "entries", {
    page: "1",
    start_date: startDate || "",
    end_date: endDate || "",
  })

  const queryParams = (overrides: Record<string, string> = {}) => ({
    end_date: endDate || "",
    page: "1",
    start_date: startDate || "",
    ...filters,
    ...overrides,
  })

  // useFetchResource intentionally exposes refetch as an unstable callback.
  // Re-fetch only when report date inputs change.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    refetch(queryParams())
  }, [endDate, startDate])
  /* eslint-enable react-hooks/exhaustive-deps */

  const updateFilter = (name: keyof typeof filters, value: string) => {
    const nextFilters = { ...filters, [name]: value }
    setFilters(nextFilters)
    refetch(queryParams({ ...nextFilters, page: "1" }))
  }

  const exportCsv = async () => {
    if (!startDate || !endDate) {
      toast("Start date and end date are required for export", "danger")
      return
    }

    setIsExporting(true)
    try {
      const params = new URLSearchParams({
        method: "payout_reconciliation",
        start_date: startDate,
        end_date: endDate,
        ...filters,
      })
      const response = await fetch(`${apiExports.index.path({ venueSlug })}?${params.toString()}`, {
        headers: { Accept: "text/csv" },
      })
      if (!response.ok) throw new Error((await response.json()).errors?.[0] || "CSV export failed")

      const blob = new Blob([await response.text()], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `catch-payout-reconciliation-${moment().format("YYYY-MM-DD-HHmmss")}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      toast(error instanceof Error ? error.message : "CSV export failed", "danger")
    } finally {
      setIsExporting(false)
    }
  }

  const columns = useMemo(
    () => [
      {
        key: "availableOn" as const,
        header: "Payout Date",
        formatter: (_value: string | null, entry: StripeConnectedPayoutEntry) =>
          entry.payout.arrivalDate ? moment(entry.payout.arrivalDate).format("MMM Do YYYY") : "—",
      },
      {
        key: "payout" as const,
        header: "Stripe Payout",
        formatter: (payout: StripeConnectedPayoutEntry["payout"]) => (
          <Link to={`/payments/payouts/${payout.id}`}>{payout.stripePayoutId}</Link>
        ),
      },
      {
        key: "payment" as const,
        header: "Related Payment",
        formatter: (payment: StripeConnectedPayoutEntry["payment"]) =>
          payment ? (
            <Link to={`/payments/${payment.id}`}>INV-{payment.id.slice(0, 6).toUpperCase()}</Link>
          ) : (
            "Unmatched"
          ),
      },
      {
        key: "relatedInvoiceId" as const,
        header: "Related Invoice",
        formatter: (invoiceId: string | null) => invoiceId || "—",
      },
      {
        key: "amountCents" as const,
        header: "Gross Payment",
        formatter: (value: number, entry: StripeConnectedPayoutEntry) =>
          formatCurrency({ cents: value, currency_iso: entry.currency }),
      },
      {
        key: "processingFeeCents" as const,
        header: "Catch Processing Fee",
        formatter: (value: number, entry: StripeConnectedPayoutEntry) =>
          formatCurrency({ cents: value, currency_iso: entry.currency }),
      },
      {
        key: "netAmountCents" as const,
        header: "Net Payout",
        formatter: (value: number, entry: StripeConnectedPayoutEntry) =>
          formatCurrency({ cents: value, currency_iso: entry.currency }),
      },
      { key: "reconciliationStatus" as const, header: "Reconciliation" },
    ],
    []
  )

  return (
    <LoadingWrapper isLoading={isLoading}>
      <div className="d-flex flex-wrap gap-2 justify-content-between mb-3">
        <div className="d-flex gap-2 flex-wrap">
          <select
            className="form-select w-auto"
            value={filters.status}
            onChange={(event) => updateFilter("status", event.target.value)}
          >
            <option value="">All payout statuses</option>
            <option value="pending">Pending</option>
            <option value="in_transit">In transit</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
          </select>
          <select
            className="form-select w-auto"
            value={filters.currency}
            onChange={(event) => updateFilter("currency", event.target.value)}
          >
            <option value="">All currencies</option>
            <option value="usd">USD</option>
            <option value="cad">CAD</option>
          </select>
          <select
            className="form-select w-auto"
            value={filters.reconciliation_status}
            onChange={(event) => updateFilter("reconciliation_status", event.target.value)}
          >
            <option value="">Matched and unmatched</option>
            <option value="matched">Matched</option>
            <option value="unmatched">Unmatched</option>
          </select>
          <input
            className="form-control w-auto"
            placeholder="Connected account ID"
            value={filters.connected_account}
            onChange={(event) => updateFilter("connected_account", event.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={exportCsv}
          disabled={isExporting}
        >
          {isExporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      <CatchCard>
        <p className="text-muted mb-3">
          Processing fee is the single amount withheld from Catch proceeds.
        </p>
        <CatchTable
          data={entries}
          columns={columns}
          emptyMessage="No payout entries"
          rowKey={(entry) => entry.id}
        />
        {metadata && (
          <Pagination
            metadata={metadata}
            onPageChange={(page) => refetch(queryParams({ page: String(page) }))}
          />
        )}
      </CatchCard>
    </LoadingWrapper>
  )
}

export default PayoutReconciliationReport
